import { useState } from "react";

// Create a company: name + identifier (the tenant key) + the first
// administrator, who is invited by email and sets their own password.
// Every field is required — a tenant with no administrator can't be used.
function AddCompanyModal({ onAddClicked, onCancelClicked }) {
  const [name, setName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [errors, setErrors] = useState({});

  const submit = () => {
    const trimmedName = name.trim();
    const id = identifier.trim().toLowerCase();
    const trimmedAdminName = adminName.trim();
    const trimmedAdminEmail = adminEmail.trim().toLowerCase();

    // Validate every field at once so all offending inputs turn red together.
    const next = {};
    if (!trimmedName) next.name = "Company name is required";
    if (!id) next.identifier = "Identifier is required";
    else if (!/^[a-z0-9-]{2,40}$/.test(id))
      next.identifier = "2-40 lowercase letters, numbers or hyphens";
    if (!trimmedAdminName) next.adminName = "Administrator full name is required";
    if (!trimmedAdminEmail) next.adminEmail = "Administrator email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedAdminEmail))
      next.adminEmail = "Enter a valid email address";

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onAddClicked({
      name: trimmedName,
      identifier: id,
      adminName: trimmedAdminName,
      adminEmail: trimmedAdminEmail,
    });
  };

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: undefined }));

  const inputBox = (hasError) =>
    `w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center px-[15px] ${
      hasError ? "border-red-500" : ""
    }`;

  return (
    <div
      onClick={onCancelClicked}
      className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-[448px] bg-white rounded-[6px] p-[18px] flex flex-col">
        <p className="text-[16px] font-OpenSansSemiBold text-titleColor mb-5">ADD TENANT</p>

        <div className="flex flex-col gap-4">
          <div className="w-full">
            <p className="text-[12px] leading-[18px] font-OpenSansSemiBold text-titleColor mb-2">Company Name</p>
            <div className={inputBox(errors.name)}>
              <input
                value={name}
                type="text"
                onChange={(e) => {
                  setName(e.target.value);
                  clearError("name");
                }}
                className="w-full text-inputEmailTitle text-[12px] font-OpenSansRegular"
                placeholder="Enter company name"
              />
            </div>
            {errors.name && (
              <p className="text-[11px] font-OpenSansRegular text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          <div className="w-full">
            <p className="text-[12px] leading-[18px] font-OpenSansSemiBold text-titleColor mb-2">
              Identifier (tenant key — cannot be changed later)
            </p>
            <div className={inputBox(errors.identifier)}>
              <input
                value={identifier}
                type="text"
                onChange={(e) => {
                  setIdentifier(e.target.value);
                  clearError("identifier");
                }}
                className="w-full text-inputEmailTitle text-[12px] font-OpenSansRegular"
                placeholder="e.g. acme (lowercase, no spaces)"
              />
            </div>
            {errors.identifier && (
              <p className="text-[11px] font-OpenSansRegular text-red-500 mt-1">{errors.identifier}</p>
            )}
          </div>

          <div className="w-full">
            <p className="text-[12px] leading-[18px] font-OpenSansSemiBold text-titleColor mb-2">
              Administrator Full Name
            </p>
            <div className={inputBox(errors.adminName)}>
              <input
                value={adminName}
                type="text"
                onChange={(e) => {
                  setAdminName(e.target.value);
                  clearError("adminName");
                }}
                className="w-full text-inputEmailTitle text-[12px] font-OpenSansRegular"
                placeholder="Enter administrator full name"
              />
            </div>
            {errors.adminName && (
              <p className="text-[11px] font-OpenSansRegular text-red-500 mt-1">{errors.adminName}</p>
            )}
          </div>

          <div className="w-full">
            <p className="text-[12px] leading-[18px] font-OpenSansSemiBold text-titleColor mb-2">
              Administrator Email (invited by email)
            </p>
            <div className={inputBox(errors.adminEmail)}>
              <input
                value={adminEmail}
                type="email"
                onChange={(e) => {
                  setAdminEmail(e.target.value);
                  clearError("adminEmail");
                }}
                className="w-full text-inputEmailTitle text-[12px] font-OpenSansRegular"
                placeholder="admin@company.com"
              />
            </div>
            {errors.adminEmail && (
              <p className="text-[11px] font-OpenSansRegular text-red-500 mt-1">{errors.adminEmail}</p>
            )}
          </div>

          <div className="w-full mt-2">
            <div className="flex items-center justify-between gap-4">
              <div
                onClick={onCancelClicked}
                className="w-[50%] h-[30px] text-[12px] font-OpenSansSemiBold rounded-[6px] border-[1px] border-subTitleColor flex items-center justify-center text-subTitleColor cursor-pointer"
              >
                CANCEL
              </div>
              <div
                onClick={submit}
                className="w-[50%] h-[30px] text-[12px] font-OpenSansSemiBold rounded-[6px] flex items-center justify-center bg-blueColor text-white cursor-pointer"
              >
                ADD
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddCompanyModal;
